import { useCallback, useEffect, useMemo, useState } from 'react';
import { IconInfoCircle } from '@tabler/icons-react';
import {
  Group,
  Stack,
  Text,
  InlineError,
  Loader,
  Tooltip,
  openConfirmDelete,
} from '@wapharma/design-system';
import { unwrapData } from '@wapharma/shared';
import { apiDeleteJson, apiGet, apiPost } from '../api';
import { getToken } from '../auth';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getDisplayError } from '../apiErrors';
import {
  capabilityPackDescriptionFr,
  capabilityPackLabelFr,
  userRoleLabelFr,
  userRolePacks,
} from '../constants/userRoleDisplay';
import { UserRolePill } from './UserRolePill';
import { StaffSessionsPanel, type StaffSessionRow } from './StaffSessionsPanel';
import styles from './AccountSettingsSection.module.css';
import settingsStyles from './SettingsOverlay.module.css';

type SessionActionResult = { endedCurrent: boolean };

function RoleCapabilitiesTooltipBody({
  roleName,
  packs,
}: {
  roleName: string;
  packs: readonly string[];
}) {
  const resolved = userRolePacks(roleName, packs);
  return (
    <div className={styles.tipCard} role="presentation">
      <Text size="sm" fw={600} className={styles.tipTitle}>
        {userRoleLabelFr(roleName)}
      </Text>
      {resolved.length > 0 ? (
        <ul className={styles.tipPacks}>
          {resolved.map((pack) => (
            <li key={pack}>
              <strong>{capabilityPackLabelFr(pack)} :</strong> {capabilityPackDescriptionFr(pack)}
            </li>
          ))}
        </ul>
      ) : (
        <Text size="xs" c="dimmed">
          Aucune capacité listée.
        </Text>
      )}
    </div>
  );
}

/**
 * Paramètres → Compte: read-only rôle + own intranet sessions.
 */
export function AccountSettingsSection() {
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const [sessions, setSessions] = useState<StaffSessionRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const primaryRole = user?.roles?.[0] ?? null;
  const roleLabel = primaryRole ? userRoleLabelFr(primaryRole) : null;
  const packs = useMemo(() => user?.packs ?? [], [user?.packs]);

  const reloadSessions = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setErr('Session requise.');
      setSessions([]);
      return;
    }
    const raw = await apiGet<{ data: StaffSessionRow[] }>('/auth/me/sessions', token);
    setSessions(unwrapData(raw) ?? []);
  }, []);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      setErr('Session requise.');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr(null);
    void reloadSessions()
      .catch((e: unknown) => {
        if (cancelled) return;
        setErr(getDisplayError(e));
        setSessions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadSessions]);

  const finishSessionAction = useCallback(
    async (result: SessionActionResult, successTitle: string) => {
      if (result.endedCurrent) {
        showToast('Session courante terminée — déconnexion.', { tone: 'info' });
        await logout();
        return;
      }
      showToast(successTitle, { tone: 'success' });
      await reloadSessions();
    },
    [logout, reloadSessions, showToast],
  );

  const executeRevoke = useCallback(
    async (row: StaffSessionRow, successTitle: string) => {
      const token = getToken();
      if (!token) throw new Error('Session requise.');
      setBusyId(row.id);
      try {
        const raw = await apiPost<{ data: SessionActionResult }>(
          `/auth/me/sessions/${row.id}/revoke`,
          {},
          token,
        );
        await finishSessionAction(unwrapData<SessionActionResult>(raw), successTitle);
      } finally {
        setBusyId(null);
      }
    },
    [finishSessionAction],
  );

  const runRevoke = useCallback(
    (row: StaffSessionRow, successTitle: string) => {
      if (!row.isCurrent) {
        void executeRevoke(row, successTitle).catch((e: unknown) => {
          showToast(getDisplayError(e), { tone: 'error' });
        });
        return;
      }
      openConfirmDelete({
        title: 'Déconnecter cette session ?',
        message: 'Déconnecter cette session met fin à votre session courante.',
        confirmLabel: 'Déconnecter',
        onConfirm: () => executeRevoke(row, successTitle),
      });
    },
    [executeRevoke, showToast],
  );

  const runDelete = useCallback(
    (row: StaffSessionRow) => {
      openConfirmDelete({
        title: 'Supprimer cette session ?',
        message: row.isCurrent
          ? 'Cela met fin à votre session courante et retire la session de l’historique.'
          : 'Supprimer définitivement cette session de l’historique ?',
        confirmLabel: 'Supprimer',
        onConfirm: async () => {
          const token = getToken();
          if (!token) throw new Error('Session requise.');
          setBusyId(row.id);
          try {
            const raw = await apiDeleteJson<{ data: SessionActionResult }>(
              `/auth/me/sessions/${row.id}`,
              token,
            );
            await finishSessionAction(unwrapData<SessionActionResult>(raw), 'Session supprimée.');
          } finally {
            setBusyId(null);
          }
        },
      });
    },
    [finishSessionAction],
  );

  return (
    <div className={settingsStyles.accountPane}>
      <Stack gap="lg">
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Rôle
          </Text>
          {primaryRole && roleLabel ? (
            <Group gap="sm" wrap="nowrap" align="center">
              <Text size="sm">Vous êtes</Text>
              <UserRolePill role={primaryRole} />
              <Tooltip
                wide
                portal
                position="bottom"
                label={<RoleCapabilitiesTooltipBody roleName={primaryRole} packs={packs} />}
              >
                <button type="button" className={styles.capInfo} aria-label={`Capacités : ${roleLabel}`}>
                  <IconInfoCircle size={16} stroke={1.7} aria-hidden />
                </button>
              </Tooltip>
            </Group>
          ) : (
            <Text size="sm" c="dimmed">
              Aucun rôle.
            </Text>
          )}
        </Stack>
        <Stack gap="sm">
          <Text size="sm" fw={600}>
            Sessions
          </Text>
          {err ? <InlineError>{err}</InlineError> : null}
          {loading ? (
            <Loader />
          ) : (
            <StaffSessionsPanel
              sessions={sessions ?? []}
              compact
              actions={{
                busyId,
                onLogout: (row) => runRevoke(row, 'Session déconnectée.'),
                onArchive: (row) => runRevoke(row, 'Session archivée.'),
                onDelete: (row) => runDelete(row),
              }}
            />
          )}
        </Stack>
      </Stack>
    </div>
  );
}
